import React, { useEffect, useMemo, useRef, useState } from 'react';
import './accordion.scss';
import { gradientFor } from './gradientFor';
import { Team } from './team/Team';
import { zynity_website } from '../../../../config/external-routes'; // ← add this import

export const Accordion = ({ org, user, headerColor, initialOpen = false }) => {
  const yii_url = zynity_website.yii_website_url;                     // ← add
  const orgId =
    org?.id ?? org?.organization_id ?? org?.orgId ?? org?.org_id ?? null;

  const orgName =
    org?.name || org?.organization || org?.org_name || org?.title || 'Organization';

  const [open, setOpen] = useState(Boolean(initialOpen));
  const g = gradientFor(headerColor);
  const containerRef = useRef(null);
  const autoOpenHandled = useRef(false);

  useEffect(() => {
    if (initialOpen && !autoOpenHandled.current) {
      autoOpenHandled.current = true;
      setOpen(true);

      const scrollToAccordion = () => {
        if (!containerRef.current) return;
        const elementTop = containerRef.current.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: Math.max(elementTop - 80, 0),
          behavior: 'smooth'
        });
      };

      // Allow layout to settle before scrolling
      window.requestAnimationFrame(() => {
        setTimeout(scrollToAccordion, 50);
      });
    }
  }, [initialOpen]);

  // Robust team extraction (unchanged)
  const teams = useMemo(() => {
    const candidates = [
      org?.teams, org?.Teams, org?.organization_teams, org?.user_organization_teams,
      org?.OrganizationTeams, org?.data,
    ];
    let t = candidates.find(Array.isArray) || [];
    if (!Array.isArray(t) && t && typeof t === 'object') {
      if (Array.isArray(t.items)) t = t.items;
      else if (Array.isArray(t.data)) t = t.data;
    }
    return Array.isArray(t) ? t : [];
  }, [org]);

  // Notifications (unchanged)
  const notifCount = useMemo(() => {
    if (typeof org?.total_notifications === 'number') return org.total_notifications;
    if (typeof org?.notifications_count === 'number') return org.notifications_count;
    if (typeof org?.unread === 'number') return org.unread;
    return teams.reduce((acc, t) => acc + (Number(t?.total_notifications) || 0), 0);
  }, [org, teams]);

  // --- role flags (best-effort fallbacks to match your API) ---
  const is_admin_from_this_org =
  Boolean(org?.is_admin_from_this_org ?? org?.is_admin ?? false);
  const is_coach_from_this_org =
  Boolean(org?.is_coach_from_this_org ?? false); // ← no user fallback

  // --- helper: submit hidden forms for Yii actions ---
  const submitForm = (id) => {
    const el = document.getElementById(id);
    if (el) el.submit();
  };

  return (
    <div ref={containerRef} className={`zn-accordion ${open ? 'is-open' : ''}`}>
      {/* Header */}
      <div
        className="zn-accordion__header"
        style={{
          backgroundImage: `linear-gradient(90deg, ${g.from}, ${g.to})`,
          color: g.text || '#fff',
        }}
      >
        <button
          className="zn-accordion__toggle"
          onClick={() => setOpen(prev => !prev)}
          aria-expanded={open}
          aria-controls={`org-panel-${orgId ?? 'x'}`}
        >
          <span aria-hidden="true" className="zn-accordion__iconbox" style={{ pointerEvents: 'none' }}>
            <span className="zn-accordion__iconglyph">🏢</span>
          </span>
          <span className="zn-accordion__title">{orgName}</span>
          {/* ↓ Arrow indicator */}
          <span
            className="zn-accordion__arrow"
            style={{
              marginLeft: 'auto',
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              display: 'inline-block'
            }}
          >
            ▼
          </span>
        </button>

        {/* Right-side: count + dropdown menu */}
        <div className="zn-accordion__actions">
          {!!notifCount && <span className="org-badge-count">{notifCount}</span>}

          <div className="dropdown">
            <button
              id={`dropdownBurgerMenu-${orgId}`}
              type="button"
              className="btn btn-light btn-sm rounded-circle px-0 org-kebab"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Organization menu"
            >
              ⋮
            </button>

            <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={`dropdownBurgerMenu-${orgId}`}>
              {is_admin_from_this_org && (
                <>
                  <li>
                    <a className="dropdown-item" href={`/organizations/${orgId}`}>
                      Admin Dashboard
                    </a>
                  </li>
                  <li>
                    <a className="dropdown-item" href={`/organizations/${orgId}/profile`}>
                      Org Profile
                    </a>
                  </li>
                </>
              )}

              {/* Add Team (Yii POST) */}
              <form
                id={`AddTeamForm${orgId}`}
                action={`${yii_url}/userHome/index`}
                method="post"
                className="d-none"
              >
                <input type="hidden" name="orgId" value={orgId} />
                <input type="hidden" name="menuAction" value="AddTeam" />
              </form>
              <li>
                <a className="dropdown-item" onClick={() => submitForm(`AddTeamForm${orgId}`)}>
                  Add Team
                </a>
              </li>

              {(is_admin_from_this_org || is_coach_from_this_org) && (
                <>
                  {/* ROI (Yii POST) */}
                  <form
                    id={`RoiForm${orgId}`}
                    action={`${yii_url}/userHome/index`}
                    method="post"
                    className="d-none"
                  >
                    <input type="hidden" name="orgId" value={orgId} />
                    <input type="hidden" name="coachId" value={user?.is_coach?.id || ''} />
                    <input type="hidden" name="coachMemberId" value={user?.id} />
                    <input type="hidden" name="menuAction" value="ROI" />
                  </form>
                  <li>
                    <a className="dropdown-item" onClick={() => submitForm(`RoiForm${orgId}`)} title="Org ROI is prepared by assigned coach">
                      ROI
                    </a>
                  </li>
                  <li>
                    <a
                      href={`${yii_url}/resources/ET_Coach_Plan.pdf`}
                      className="dropdown-item"
                      title="Org ROI is prepared by assigned coach"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Coach Plan
                    </a>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Body (unchanged) */}
      <div
        id={`org-panel-${orgId ?? 'x'}`}
        className="zn-accordion__body"
        style={{ display: open ? 'block' : 'none' }}
      >
        <div className="zn-accordion__content">
          {teams.length === 0 ? (
            <div className="text-muted" style={{ padding: '6px 4px' }}>
              No teams found for this organization.
            </div>
          ) : (
            teams.map(team => (
              <Team key={team?.id ?? `${orgId}-team`} team={team} org_id={orgId} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};
